import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CustomFile, CustomUploadedFile } from '../../../../../../../models/interfaces/custom_file.interface';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from '../../../../../../../core/services/alert/alert.service';
import { ApiService } from '../../../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../../../core/services/toast/toast.service';
import { UtilsService } from '../../../../../../../shared/utils/utils.service';
import { cloneDeep } from 'lodash-es';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EnfafModalComponent } from "../../../../../../../components/enfaf-modal/enfaf-modal.component";
import { EnfafInputFileDraggerLightComponent } from "../../../../../../../components/enfaf-input-file-dragger-light/enfaf-input-file-dragger-light.component";
import { EnfafTextareaLightComponent } from "../../../../../../../components/enfaf-textarea-light/enfaf-textarea-light.component";
import { EnfafInputComponent } from "../../../../../../../components/enfaf-input/enfaf-input.component";

@Component({
    selector: 'app-modal-final-bulk-email',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatTooltipModule,
        EnfafModalComponent,
        EnfafInputFileDraggerLightComponent,
        EnfafTextareaLightComponent,
        EnfafInputComponent,
    ],
    templateUrl: './modal-final-bulk-email.component.html',
    styleUrl: './modal-final-bulk-email.component.scss'
})
export class ModalFinalBulkEmailComponent implements OnInit, OnDestroy {
  courseId!: string;
  courseName!: string;
  showCloseAlert = false;
  emailForm: FormGroup = new FormGroup({});
  attachmentsToUpload: Array<CustomFile> = [];
  isSavingEmail = false;
  currentAttachments: Array<CustomUploadedFile> = [];

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalFinalBulkEmailComponent>,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  async confirmCloseModal(): Promise<any> {
    if (this.showCloseAlert) {
      const closeModal = await this.alert.presentCloseWindowAlert();
      return closeModal ? this.closeModal() : null;
    }

    this.closeModal();
  }

  closeModal(result?: any): void {
    this.dialogRef.close(result);
  }

  initForm(): void {
    const receivedData = this.data ? cloneDeep(this.data) : null;
    this.courseId = receivedData?.courseId;
    this.courseName = receivedData?.courseName;
    if (!this.courseId || !this.courseName) {
      this.closeModal();
      return;
    }

    if (receivedData?.finalEmailAttachments) {
      this.currentAttachments = receivedData.finalEmailAttachments;
    }

    this.emailForm = new FormGroup({
      subject: new FormControl(receivedData?.finalEmailSubject || null, Validators.required),
      message: new FormControl(receivedData?.finalEmailMessage || null, Validators.required)
    });
  }

  listenForChanges(): void {
    this.emailForm.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
      this.showCloseAlert = true;
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    });
  }

  onDroppedFiles(files: Array<CustomFile>): void {
    if (!files?.length) return;

    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }

    this.attachmentsToUpload = files;
  }

  async saveEmail(): Promise<void> {
    try {
      this.isSavingEmail = true;
      const emailFormData = new FormData();
      const body = { ...this.emailForm.value, courseId: this.courseId };
      emailFormData.append('form', JSON.stringify(body));

      if (this.attachmentsToUpload.length) {
        for (let i = 0; i < this.attachmentsToUpload.length; i++) {
          emailFormData.append(`attachment_${i}`, this.attachmentsToUpload[i].file, this.attachmentsToUpload[i].name);
        }
      }

      const response = await firstValueFrom(this.api.saveFinalBulkEmail(emailFormData));
      this.isSavingEmail = false;
      this.toast.presentSuccessToast(response.message);
      this.closeModal({ finalEmailUpdated: response.finalEmailUpdated });
    } catch (errorResponse: any) {
      this.isSavingEmail = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando email');
    }
  }

  async confirmDeleteAttachment(attachment: any): Promise<any> {
    const header = 'Borrar adjunto';
    const message = `¿Estás seguro(a) de borrar el adjunto "${attachment.filename}"? Esta acción no se podrá deshacer.`;
    const noDelete = 'Cancelar';
    const remove = 'Borrar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteAttachment(attachment) : null;
  }

  async deleteAttachment(attachment: CustomUploadedFile): Promise<void> {
    try {
      const body = {
        filename: attachment.filename,
        path: attachment.path,
        courseId: this.courseId
      };
      const response = await firstValueFrom(this.api.deleteFinalEmailCourseAttachment(body));
      this.currentAttachments = response.attachments;
      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando documento');
    }
  }
}
